import KollectiveXHR from '../interceptor/KollectiveXHR';
import logger from '../log';
import { HttpMethod } from '../interceptor/interfaces';
import {
  ErrorMessages,
  THEOPlayer,
  THEORequest,
  THEORequestListener
} from '@kollective-frontend/ksdk-api';
import { handleError } from './bindHLSjsToWebRTC';
import { InterceptBinder } from '../player/utils/InterceptBinder';

const READY_STATE_CHANGE = 'readystatechange';
const log = logger.getLogger(__filename);

export const bindRequestInterceptor = (
  binder: InterceptBinder
): THEORequestListener => {
  return (request: THEORequest): void => {
    if (!binder.shouldIntercept()) {
      log.debug('not intercepting');
      return;
    }
    try {
      const opts = binder.getOptions() || {};
      const xhr = new KollectiveXHR(opts);
      xhr.withCredentials = request.withCredentials;
      xhr.responseType = request.responseType;

      request.waitUntil(
        new Promise<void>(resolve => {
          const listener = async (evt: Event): Promise<void> => {
            if (xhr.readyState !== 4) {
              return;
            }
            log.trace('finishing request', evt);
            xhr.removeEventListener(READY_STATE_CHANGE, listener);
            request.respondWith({
              body: xhr.response,
              status: xhr.status || 0,
              statusText: xhr.statusText || ''
            });
            resolve();
          };
          xhr.addEventListener(READY_STATE_CHANGE, listener);
          xhr.open(request.method as HttpMethod, request.url);
          xhr.send(request.body as string);
        })
      );
    } catch (e) {
      log.warn('Error during intercept:', e);
    }
  };
};

const errorListener = (evt: unknown) => {
  log.info('THEOplayer experienced an error:', evt);
};

/**
 * Bind intercept to theoplayer instance through request/response interceptors.
 * https://docs.portal.theoplayer.com/how-to-guides/08-network/01-request-response-interceptors.md
 *
 * @param player - theoplayer player instance
 * @param shouldIntercept - function that returns true if intercept
 *                   should happen
 * @param getOptions - function that returns the relevant peer manager
 * @returns void
 */
export default function bindTHEOplayerToWebRTC(
  player: THEOPlayer,
  binder: InterceptBinder
): void {
  if (player && player.network) {
    const intercept = bindRequestInterceptor(binder);
    player.network.addRequestInterceptor(intercept);
    player.addEventListener('error', errorListener);
  } else {
    handleError(ErrorMessages.BAD_THEOPLAYER, log);
  }
}
